import * as THREE from "three";
import type { Game } from "../game/Game";
import { cellsFor, isSpanning } from "../game/Pieces";
import { VISUAL } from "../config/visualConfig";
import { GAME } from "../config/gameConfig";
import { MenuMaterial } from "./MenuMaterial";

export class HudRenderer {
  readonly group = new THREE.Group();
  private readonly hudCanvas = document.createElement("canvas");
  private readonly titleCanvas = document.createElement("canvas");
  private readonly hudTexture: THREE.CanvasTexture;
  private readonly titleTexture: THREE.CanvasTexture;
  private readonly titlePlane: THREE.Mesh;
  private readonly hudPlane: THREE.Mesh;
  private signature = "";
  private readonly menuMaterials: MenuMaterial[] = [];

  constructor(private readonly game: Game) {
    this.hudCanvas.width = 1024;
    this.hudCanvas.height = 220;
    this.titleCanvas.width = 1024;
    this.titleCanvas.height = 640;
    this.hudTexture = new THREE.CanvasTexture(this.hudCanvas);
    this.titleTexture = new THREE.CanvasTexture(this.titleCanvas);
    this.hudTexture.colorSpace = THREE.SRGBColorSpace;
    this.titleTexture.colorSpace = THREE.SRGBColorSpace;

    const hudMaterial = new MenuMaterial(this.hudTexture, new THREE.Vector4(10 / 1024, 15 / 220, 1014 / 1024, 205 / 220));
    const titleMaterial = new MenuMaterial(this.titleTexture, new THREE.Vector4(40 / 1024, 32 / 640, 984 / 1024, 608 / 640));
    this.menuMaterials.push(hudMaterial, titleMaterial);
    this.hudPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(9.6, 2.06),
      hudMaterial,
    );
    this.hudPlane.position.set(0, -8.05, 1.9);
    this.hudPlane.rotation.x = -VISUAL.cameraPitch;
    this.hudPlane.renderOrder = 20;

    this.titlePlane = new THREE.Mesh(
      new THREE.PlaneGeometry(5.8, 3.62),
      titleMaterial,
    );
    this.titlePlane.position.set(0, 1.2, 0.15);
    this.titlePlane.renderOrder = 30;
    this.group.add(this.hudPlane, this.titlePlane);
    this.update(true);
  }

  update(force = false): void {
    const nextSignature = [this.game.phase, this.game.mode, this.game.score, this.game.lines, this.game.level, this.game.nextType, this.game.foregroundLayer, this.game.active?.type, (GAME.initialGravityMs / this.game.gravityMs).toFixed(1)].join(":");
    if (!force && nextSignature === this.signature) return;
    this.signature = nextSignature;
    this.drawHud();
    this.drawTitle();
  }

  animate(elapsed: number): void {
    for (const material of this.menuMaterials) material.scrollTime.value = elapsed;
  }

  setGameOverAnimating(active: boolean): void {
    this.titlePlane.visible = this.game.phase !== "playing" && !active;
  }

  setView(yaw: number, pitch: number): void {
    const orientation = new THREE.Quaternion().setFromEuler(new THREE.Euler(-pitch, yaw, 0, "YXZ"));
    const offset = orientation.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(VISUAL.cameraPitch, 0, 0)));
    this.hudPlane.position.set(0, -8.05, 1.9).applyQuaternion(offset);
    this.titlePlane.position.set(0, 1.2, 0.15).applyQuaternion(offset);
    this.hudPlane.quaternion.copy(orientation);
    this.titlePlane.quaternion.copy(orientation);
  }

  private drawHud(): void {
    const context = this.hudCanvas.getContext("2d")!;
    context.clearRect(0, 0, this.hudCanvas.width, this.hudCanvas.height);
    context.fillStyle = "rgba(2, 8, 16, .72)";
    context.fillRect(10, 15, 1004, 190);
    context.strokeStyle = "rgba(53, 234, 255, .45)";
    context.lineWidth = 2;
    context.strokeRect(10, 15, 1004, 190);
    context.font = "700 42px monospace";
    context.textBaseline = "middle";
    context.fillStyle = "#dffcff";
    context.fillText("DUAL", 45, 66);
    context.fillStyle = "#ff42dd";
    context.fillText("//", 174, 66);
    context.fillStyle = "#dffcff";
    context.fillText("STACK", 226, 66);
    const layer = this.game.foregroundLayer;
    const spanning = Boolean(this.game.active && isSpanning(this.game.active.type));
    context.font = "700 25px monospace";
    context.fillStyle = `#${(layer === "front" ? VISUAL.front : VISUAL.back).toString(16).padStart(6, "0")}`;
    context.fillText(layer === "front" ? "FRONT / CYAN" : "FRONT / RED", 420, 66);
    if (spanning) {
      context.font = "700 20px monospace";
      context.fillStyle = `#${VISUAL.spanning.toString(16)}`;
      context.fillText("BOTH / PURPLE", 420, 97);
    }
    context.font = "700 20px monospace";
    context.fillStyle = "#83dfe8";
    context.fillText(this.game.mode === "alternate" ? "ALT SET" : "CORE SET", 45, 97);
    context.fillText(`SPEED ×${(GAME.initialGravityMs / this.game.gravityMs).toFixed(1)}`, 670, 97);
    context.font = "24px monospace";
    context.fillStyle = "#58dfea";
    context.fillText(`SCORE ${String(this.game.score).padStart(6, "0")}`, 45, 144);
    context.fillText(`LV ${String(this.game.level).padStart(2, "0")}`, 390, 144);
    context.fillText(`LINES ${String(this.game.lines).padStart(2, "0")}`, 540, 144);
    context.fillStyle = "#708993";
    context.fillText("NEXT", 805, 55);
    this.drawNext(context, 866, 133);
    this.hudTexture.needsUpdate = true;
  }

  private drawNext(context: CanvasRenderingContext2D, centerX: number, centerY: number): void {
    const cells = cellsFor(this.game.nextType, 0);
    const minX = Math.min(...cells.map((cell) => cell.x));
    const maxX = Math.max(...cells.map((cell) => cell.x));
    const minY = Math.min(...cells.map((cell) => cell.y));
    const maxY = Math.max(...cells.map((cell) => cell.y));
    const size = 28;
    const offsetX = centerX - ((maxX - minX + 1) * size) / 2;
    const offsetY = centerY - ((maxY - minY + 1) * size) / 2;
    context.fillStyle = `#${(isSpanning(this.game.nextType) ? VISUAL.spanning : this.game.foregroundLayer === "front" ? VISUAL.front : VISUAL.back).toString(16).padStart(6, "0")}`;
    for (const cell of cells) {
      context.globalAlpha = 0.78;
      context.fillRect(offsetX + (cell.x - minX) * size + 2, offsetY + (cell.y - minY) * size + 2, size - 4, size - 4);
      context.globalAlpha = 1;
    }
  }

  private drawTitle(): void {
    const context = this.titleCanvas.getContext("2d")!;
    context.clearRect(0, 0, this.titleCanvas.width, this.titleCanvas.height);
    this.titlePlane.visible = this.game.phase !== "playing";
    if (this.game.phase === "playing") {
      this.titleTexture.needsUpdate = true;
      return;
    }
    context.fillStyle = "rgba(1, 5, 13, .78)";
    context.fillRect(40, 32, 944, 576);
    context.strokeStyle = this.game.phase === "game-over" ? "#ff4776" : "#42eafa";
    context.lineWidth = 4;
    context.strokeRect(40, 32, 944, 576);
    context.textAlign = "center";
    context.textBaseline = "middle";
    if (this.game.phase === "game-over") {
      context.fillStyle = "#ff4776";
      context.font = "700 62px monospace";
      context.fillText("SYSTEM FAILURE", 512, 192);
      context.fillStyle = "#dffcff";
      context.font = "700 92px monospace";
      context.fillText("STACK OVERFLOW", 512, 300);
      context.font = "32px monospace";
      context.fillStyle = "#ff92ab";
      context.fillText(`SCORE ${String(this.game.score).padStart(6, "0")}`, 512, 402);
    } else if (this.game.phase === "paused") {
      context.fillStyle = "#dffcff";
      context.font = "700 100px monospace";
      context.fillText("PAUSED", 512, 270);
      context.font = "30px monospace";
      context.fillStyle = "#58dfea";
      context.fillText("ESC TO RESUME", 512, 390);
    } else {
      context.fillStyle = "#dffcff";
      context.font = "700 126px monospace";
      context.fillText("DUAL", 512, 170);
      context.fillStyle = "#ff42dd";
      context.fillText("//", 512, 296);
      context.fillStyle = "#dffcff";
      context.fillText("STACK", 512, 422);
    }
    if (this.game.phase !== "paused") {
      context.font = "30px monospace";
      context.fillStyle = "#58dfea";
      context.fillText("PRESS SPACE", 512, 535);
    }
    this.titleTexture.needsUpdate = true;
  }
}
